import React, { useEffect, useState } from 'react';
import { X, Check, Mail, User, Phone, Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import { apiList, invokeApi } from '../../../../../apis/apiServices';
import { config } from '../../../../../config/config';
import mobileNoValidation, { countryCodeList } from '../../../../../common/common';
import '../../../../end-user/PropertyDetails/LeadModal.css';

const PainterAutoPopup = ({ isOpen, onClose, productUrl = '' }) => {
  const [cookies] = useCookies();
  const { userData, userError } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({
    customerName: userData?.user?.fullName || '',
    countryCode: '91',
    mobileNumber: userData?.user?.mobileNumber || '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [animate, setAnimate] = useState(false);

  // Check persistent submission flag on mount / open
  const leadAlreadySubmitted = localStorage.getItem('painterLeadSubmitted') === 'true';

  const mobileValidate = mobileNoValidation(formData.mobileNumber);

  useEffect(() => {
    if (isOpen) {
      // If a lead was already submitted, do not show the modal at all
      if (leadAlreadySubmitted) {
        onClose();
        return;
      }
      document.body.style.overflow = 'hidden';
      setTimeout(() => setAnimate(true), 50);
      setIsSubmitted(false);
      setErrors({});
      setFormData((prev) => ({
        ...prev,
        customerName: userData?.user?.fullName || prev.customerName || '',
        mobileNumber: userData?.user?.mobileNumber || prev.mobileNumber || '',
      }));
    } else {
      document.body.style.overflow = 'auto';
      setAnimate(false);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, userData, leadAlreadySubmitted, onClose]);

  useEffect(() => {
    if (userError) {
      alert('Something went wrong while fetching user details. Please try again later!');
    }
  }, [userError]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Full name is required';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!mobileValidate) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleMobileChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, mobileNumber: value }));
    if (errors.mobileNumber) setErrors((prev) => ({ ...prev, mobileNumber: '' }));
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      countryCode: '91',
      mobileNumber: '',
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const params = {
      customerName: formData.customerName,
      countryCode: formData.countryCode,
      mobileNumber: formData.mobileNumber,
      propertyId: '',
      leadSource: 'Painter Service',
      leadType: 'Own',
      productUrl,
    };

    try {
      const response = await invokeApi(config.apiDomains + apiList.addLead, params, cookies);
      if (
        response?.status >= 200 &&
        response?.status < 300 &&
        response?.data?.responseCode === '200'
      ) {
        toast.success(
          'Thank you for showing interest. Our painting expert will contact you soon.',
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            transition: Bounce,
          }
        );
        // Persist the submission so the popup never shows again
        localStorage.setItem('painterLeadSubmitted', 'true');
        resetForm();
        setIsSubmitted(true);
      } else {
        toast.error('Sorry something went wrong. Please try again.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error('Sorry something went wrong. Please try again.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        transition: Bounce,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If lead already submitted, don't render the modal at all
  if (!isOpen || leadAlreadySubmitted) return null;

  const canClose = isSubmitted; // modal can be closed only after successful submit

  return (
    <div
      className={`lead-modal-overlay ${animate ? 'active' : ''}`}
      onClick={() => {
        if (canClose) onClose();
      }}
    >
      <div
        className={`lead-modal-container painter-modal-container ${animate ? 'active' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!isSubmitted && (
          <button
            className="lead-modal-close"
            type="button"
            onClick={() => {
              if (canClose) onClose();
            }}
            disabled={!canClose}
            aria-disabled={!canClose}
            style={{ visibility: canClose ? 'visible' : 'hidden' }}
          >
            <X size={24} />
          </button>
        )}

        <div className="lead-modal-content">
          {!isSubmitted ? (
            <>
              <div className="lead-modal-header">
                <div className="lead-modal-badge">Free Consultation</div>
                <h2 className="lead-modal-title">Book Your Free Painting Consultation</h2>
                <p className="lead-modal-subtitle">
                  Share your details and our painting specialist will call you back with a no-obligation quote.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="lead-modal-form">
                <div className="form-group">
                  <label htmlFor="painterCustomerName">Full Name</label>
                  <div className="input-wrapper">
                    <User size={18} className="input-icon" />
                    <input
                      type="text"
                      id="painterCustomerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className={errors.customerName ? 'error' : ''}
                    />
                  </div>
                  {errors.customerName && (
                    <span className="error-message">{errors.customerName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="painterMobileNumber">Mobile Number</label>
                  <div className="phone-input-container">
                    <div className="country-code-wrapper">
                      <select
                        id="painterCountryCode"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="country-code-select"
                      >
                        {countryCodeList.map((country) => (
                          <option key={country.code} value={country.code}>
                            +{country.code} {country.iso}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="phone-wrapper">
                      <Phone size={18} className="input-icon phone-icon" />
                      <input
                        type="tel"
                        id="painterMobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleMobileChange}
                        placeholder="1234567890"
                        maxLength={10}
                        inputMode="numeric"
                        className={errors.mobileNumber ? 'error' : ''}
                      />
                    </div>
                  </div>
                  {errors.mobileNumber && (
                    <span className="error-message">{errors.mobileNumber}</span>
                  )}
                </div>

                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="button-loading">Processing...</span>
                  ) : (
                    <>
                      <span>Get Free Quote</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p className="privacy-note">
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                  <Link to="/terms-conditions">Terms of Service</Link>.
                </p>
              </form>
            </>
          ) : (
            <div className="lead-modal-success">
              <div className="success-icon">
                <Check size={32} />
              </div>
              <h2>Thanks! We’ll contact you soon</h2>
              <p>
                Your painting request has been received. Our expert will contact you shortly
                with the best options for your space.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PainterAutoPopup;